import { intersection, isEmpty, isNil } from "lodash";
import { useBookingModuleContext } from "PFApp/booking/parts/providers/booking_module_context_provider";
import SortBy from "PFApp/components/sort_by";
import { DropdownOption } from "PFComponents/dropdown/dropdown";
import { OrderOption } from "PFTypes";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { WorkforceCalendarData } from "../../context/booking_overview_context.types";
import { useBookingRowsContext } from "../../context/booking_rows_context";
import { useBookingsOverviewContext } from "../../context/bookings_overview_context";
import css from "./actions_bar.module.scss";
import { EntriesCounter } from "./entries_counter";

const TOTAL_LIMIT_BY_ORDER_NAME = {
  billable_utilization: 3000, // eslint-disable-line camelcase
  available_minutes: 3000 // eslint-disable-line camelcase
};

const ORDER_OPTIONS_WITH_CALENDAR_RANGE = ["billable_utilization", "available_minutes"];

const getOrderId = (option: OrderOption | undefined): string => `${option?.name?.[0]}_${option?.direction}`;

export const ActionsBar = React.forwardRef<HTMLDivElement | null>((props, ref) => {
  const {
    calendarData: { data, onOrderChange }
  } = useBookingsOverviewContext<WorkforceCalendarData>();
  const { isEditModeEnabled } = useBookingRowsContext();
  const { calendarRange } = useBookingModuleContext();
  const { t } = useTranslation("bookingModule", { keyPrefix: "calendar" });

  useEffect(() => {
    calendarRange &&
      onOrderChange((oldValue) => ({
        ...oldValue,
        start_date: calendarRange.min, // eslint-disable-line camelcase
        end_date: calendarRange.max // eslint-disable-line camelcase
      }));
  }, [calendarRange?.min, calendarRange?.max]);

  const sortByOptions: DropdownOption[] = useMemo(
    () =>
      (data.meta.order?.options || []).map((option) => {
        const limits = option.name
          .map((name) => TOTAL_LIMIT_BY_ORDER_NAME[name])
          .filter((limit) => !isNil(limit));
        const isDisabled =
          !isEmpty(limits) && (data.meta.counters?.filtered_profiles_count as number) > Math.min(...limits);
        return {
          id: getOrderId(option),
          item: option,
          displayElement: option.text,
          disabled: isDisabled,
          tooltipMsg: isDisabled ? t("sorting.disabledTooltip") : undefined
        };
      }),
    [data.meta.order, data.meta.counters?.filtered_profiles_count]
  );

  const handleSortByChange = (value) => {
    if (intersection(ORDER_OPTIONS_WITH_CALENDAR_RANGE, value?.name || []).length) {
      onOrderChange({
        ...value,
        start_date: calendarRange.min, // eslint-disable-line camelcase
        end_date: calendarRange.max // eslint-disable-line camelcase
      });
    } else {
      onOrderChange(value);
    }
  };

  return (
    <div className={css.actionsBar} ref={ref}>
      <div className={css.actionsBarSortingContainer}>
        {!isEmpty(sortByOptions) && (
          <SortBy
            sort={getOrderId(data.meta.order?.value)}
            options={sortByOptions}
            onSortChange={handleSortByChange}
            inputInnerStyle={{ border: "none", height: 35 }}
            dropdownStyle={{ maxWidth: 200, maxHeight: 200 }}
            noLabel
            classes={{
              selectRoot: css.sortBySelectRoot
            }}
            disabled={isEditModeEnabled}
          />
        )}
        <EntriesCounter />
      </div>
    </div>
  );
});

ActionsBar.displayName = "ActionsBar";
